import { SCondition, RequestQueryBuilder } from '@nestjsx/crud-request';
import { EoProgressSubscriptions } from '../../@crt/models/mods/eo-progress-subscriptions.model';
import { EoProgressComments } from '../../@crt/models/mods/eo-progress-comments.model';
import { EoProgressExclusionList } from '../../@crt/models/mods/eo-progress-exclusion-list.model';
import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import {Burly} from 'kb-burly';
import {SortDirection} from '@angular/material/sort';
import {Observable, of} from 'rxjs';
import {PaginationResponse} from '../../../responses/pagination.response';
import {map, tap} from 'rxjs/operators';
import { EoCompletionComparisonResponse } from '../responses/eo-completion-comparison.response';
import {EoCompletionComparison} from '../../@crt/models/mods/eo-completion-comparison.model';
import {ManyResponse} from '../../../responses/many.response';

@Injectable({
  providedIn: 'root'
})
export class TorchEoCompletionComparisonService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }

  public getEoCompletionComparisons(pageSize: number = 50,
                                    currentPage: number = 0,
                                    sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
                                    sortBy: string = 'ID',
                                    filters: SCondition | null): Observable<EoCompletionComparisonResponse> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection && sortDirection != null) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eo-completion-comparison')
      .appendRawQueryString(qb.query(false));

    finalURL = endpoint.get;

    return this.httpClient.get<PaginationResponse>(finalURL);
  }

  public exportToExcel(eoComparisons: EoCompletionComparison[]) {
    return new Observable(subscriber => {
      let endpoint = Burly(this.config.rootUrl)
        .addSegment('/eo-completion-comparison')
        .addSegment('/export')
        .get;

      const httpOptions: { headers; observe; responseType; } = {
        headers: new HttpHeaders({
          'Content-Type':  'application/json'
        }),
        observe: 'response',
        responseType: 'blob'
      };

      const body = {
        eoComparisons: eoComparisons
      }

      this.httpClient.post(endpoint, body, httpOptions).subscribe((response) => {
        subscriber.next((response as unknown as HttpResponse<any>).body);
        subscriber.complete();
      });
    });
  }

  public getModFleets(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eo-completion-comparison')
      .addSegment('/modFleets')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ modFleet: string }]>(endpoint).pipe(
      map(response => response.map(v => v.modFleet))
    );
  }

  public getDifferenceNegatives(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eo-completion-comparison')
      .addSegment('/differenceNegative')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ differenceNegative: string }]>(endpoint).pipe(
      map(response => response.map(v => v.differenceNegative))
    );
  }

  public deleteEoProgressExclusion(eo: EoProgressExclusionList) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-exclusions')
      .addSegment('/' + eo.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((eo) => {
          resolve(eo);
        }, reject);
    });
  }

  public addEoProgressExclusion(eo: EoProgressExclusionList) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-exclusions')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, eo)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public getEoProgressExclusion(filter: string | string[]) {
    let endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-exclusions')
      .get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              endpointURL = `${endpointURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              endpointURL = `${endpointURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        endpointURL = `${endpointURL}&filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<ManyResponse<EoProgressExclusionList>>(endpointURL);
  }

  public saveEoProgress(eoProgress: EoCompletionComparison) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-completion-comparison')
      .addSegment('/' + eoProgress.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.patch(endpointURL, eoProgress)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public addEoProgressComments(eo: EoProgressComments) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-comments')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, eo)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public addEoProgressCommentsToLog(eo: EoProgressComments) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-comments-log')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, eo)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public updateEoProgressComments(eo: EoProgressComments) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-comments')
      .addSegment('/' + eo.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.patch(endpointURL, eo)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public getEoProgressComments(filter: string | string[]) {
    let endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-comments')
      .get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              endpointURL = `${endpointURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              endpointURL = `${endpointURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        endpointURL = `${endpointURL}&filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<ManyResponse<EoProgressComments>>(endpointURL);
  }

  public deleteEoProgressComments(eo: EoProgressComments) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-comments')
      .addSegment('/' + eo.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((eo) => {
          resolve(eo);
        }, reject);
    });
  }

  public deleteEoProgressCommentsFromLog(eo: EoProgressComments) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-comments-log')
      .addSegment('/' + eo.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((eo) => {
          resolve(eo);
        }, reject);
    });
  }

  public addEoProgressSub(sub: EoProgressSubscriptions) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-subs')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, sub)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public deleteEoProgressSub(id: number) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-subs')
      .addSegment('/' + id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((eo) => {
          resolve(eo);
        }, reject);
    });
  }

  public getEoProgressSub(filter: string | string[]) {
    let endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eo-progress-subs')
      .get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              endpointURL = `${endpointURL}?filter=${encodeURIComponent(filterValue)}`;
            } else {
              endpointURL = `${endpointURL}&filter=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        endpointURL = `${endpointURL}?filter=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<ManyResponse<EoProgressSubscriptions>>(endpointURL);
  }
}
